// * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// * Copyright 2023 The Geek-AI Authors. All rights reserved.
// * Use of this source code is governed by a Apache-2.0 license
// * that can be found in the LICENSE file.
// * @Author yangjian102621@163.com
// * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

import { createRouter, createWebHistory } from "vue-router";
import { checkSession } from "@/store/cache";
import { useSharedStore } from "@/store/sharedata";
import { getUserToken } from "@/store/session";
import pinia from "@/store/pinia";

// 导入路由模块
import {
  commonRoutes,
  authRoutes,
  specialRoutes,
} from "@/router/modules/common";
import { homeRoutes } from "@/router/modules/home";
import { adminRoutes } from "@/router/modules/admin";
import { educatorRoutes } from "@/router/modules/educator";
import { studentRoutes } from "@/router/modules/student";
// import { mobileRoutes } from "@/router/modules/mobile";

// 合并所有路由
const routes = [
  ...commonRoutes,
  ...authRoutes,
  ...homeRoutes,
  ...adminRoutes,
  ...educatorRoutes,
  ...studentRoutes,
  // ...mobileRoutes,
  ...specialRoutes,
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
});

let prevRoute = null;

// 根据角色获取对应的登录页面路径
function getLoginPath(targetPath) {
  if (targetPath.startsWith("/educator")) {
    return "/educator/login";
  } else if (targetPath.startsWith("/student")) {
    return "/student/login";
  } else if (targetPath.startsWith("/admin")) {
    return "/admin/login";
  }
  return "/login";
}

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title;
  prevRoute = from;

  const store = useSharedStore(pinia);

  // 同步 room_id 到 store
  if (to.query && to.query.room_id) {
    store.setCurRoomId(to.query.room_id);
  }

  // 优先从持久化存储读取登录状态和角色
  let userRole = store.userRole;
  let isLogin = store.isLogin;
  const hasToken = !!getUserToken();

  // 如果有 token 但 store 中没有登录状态，尝试验证 session
  if (hasToken && (!isLogin || !userRole)) {
    try {
      const user = await checkSession();
      if (user) {
        isLogin = true;
        store.setIsLogin(true);
        userRole = store.setUserRole(user.role);
      } else {
        // session 无效，清除认证状态
        store.clearAuth();
        isLogin = false;
        userRole = null;
      }
    } catch (error) {
      // session 验证失败，清除认证状态
      store.clearAuth();
      isLogin = false;
      userRole = null;
    }
  } else if (!hasToken) {
    // 没有 token，确保 store 状态一致
    if (isLogin) {
      store.clearAuth();
    }
    isLogin = false;
    userRole = null;
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiredRoles = to.matched.reduce((roles, record) => {
    return roles.concat(record.meta.roles || []);
  }, []);

  if (requiresAuth) {
    if (!isLogin) {
      // 需要认证但未登录，跳转到对应的登录页
      const loginPath = getLoginPath(to.path);
      next({
        path: loginPath,
        query: { returnUrl: encodeURIComponent(to.fullPath) },
      });
    } else if (
      requiredRoles.length > 0 &&
      (!userRole || !requiredRoles.includes(userRole))
    ) {
      // 需要特定角色但用户角色不匹配
      next("/403");
    } else {
      // 权限满足，允许访问
      next();
    }
  } else {
    // 不需要认证的页面，直接允许访问
    next();
  }
});

export { router, prevRoute };
